import { createElement, Fragment } from 'react'
import type { ReactNode } from 'react'
import { plainText } from './text'

/**
 * HTML del PMS renderizado con lista blanca (D49): la app actual lo pinta con `html-react-parser` sin sanear
 * (ListItem.tsx, ListItemDetails.tsx). v2 lo recorre con DOMParser y crea elementos de React solo con estas
 * etiquetas; nunca inyecta HTML (`dangerouslySetInnerHTML`) ni copia atributos, salvo `href` seguro en los enlaces.
 * - Se conservan: párrafos, saltos, negrita, cursiva, subrayado, listas y enlaces http(s), mailto y tel.
 * - Los títulos del PMS pasan a párrafo en negrita, para no romper el orden de títulos de la pantalla.
 * - Se eliminan con su contenido: script, style, iframe, object, embed, template, noscript, svg y math.
 * - Imágenes fuera: las fotos de la guía van aparte. Cualquier otra etiqueta se desenvuelve (queda su texto).
 */
const KEEP: Record<string, string> = { p: 'p', br: 'br', strong: 'strong', b: 'strong', em: 'em', i: 'em', u: 'u', ul: 'ul', ol: 'ol', li: 'li' }
const HEADINGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'])
const DROP = new Set(['script', 'style', 'iframe', 'object', 'embed', 'template', 'noscript', 'svg', 'math', 'img', 'video', 'audio', 'form', 'input', 'button', 'select', 'textarea', 'head', 'title', 'meta', 'link'])
const SAFE_HREF = /^(https?:|mailto:|tel:)/i

/** true si el texto trae alguna etiqueta HTML. */
export const hasMarkup = (html: string) => /<[a-z][\s\S]*>/i.test(html)

function convert(node: Node, key: string): ReactNode {
  if (node.nodeType === 3) return node.textContent
  if (node.nodeType !== 1) return null
  const el = node as Element
  const tag = el.tagName.toLowerCase()
  if (DROP.has(tag)) return null
  const children = Array.from(el.childNodes).map((child, i) => convert(child, `${key}.${i}`))
  if (tag === 'br') return createElement('br', { key })
  if (tag === 'a') {
    const href = el.getAttribute('href')?.trim() ?? ''
    if (!SAFE_HREF.test(href)) return createElement(Fragment, { key }, ...children)
    const external = /^https?:/i.test(href)
    return createElement('a', { key, href, ...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {}) }, ...children)
  }
  if (HEADINGS.has(tag)) return createElement('p', { key }, createElement('strong', null, ...children))
  const mapped = KEEP[tag]
  if (mapped) return createElement(mapped, { key }, ...children)
  return createElement(Fragment, { key }, ...children)
}

/** Nodos de React a partir del HTML del PMS, con la lista blanca de arriba. Texto sin etiquetas: tal cual. */
export function richText(html: string): ReactNode {
  if (!hasMarkup(html)) return html
  if (typeof DOMParser === 'undefined') return plainText(html)
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return Array.from(doc.body.childNodes).map((child, i) => convert(child, String(i)))
}
